/**
 * TanStack Query hooks — typed wrappers over the API client.
 * Each page uses these hooks; server-state is cached, invalidated after
 * mutations, and keyed per tenant (query keys always start with the
 * active tenant code).
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { get, post, patch, remove, list, toApiError } from "./client";
import { useAuthStore } from "@/lib/auth/store";

export function tenantKey(parts: string[]): string[] {
  const code = useAuthStore.getState().activeTenantCode ?? "default";
  return [code, ...parts];
}

export function useApiQuery<T>(
  key: string[],
  path: string,
  options?: Omit<UseQueryOptions<T, Error>, "queryKey" | "queryFn">,
) {
  return useQuery<T, Error>({
    queryKey: tenantKey(key),
    queryFn: async () => {
      try {
        return await get<T>(path);
      } catch (err) {
        throw toApiError(err);
      }
    },
    ...options,
  });
}

export function useApiList<T>(
  key: string[],
  path: string,
  options?: {
    enabled?: boolean;
    retry?: UseQueryOptions<unknown, Error>["retry"];
  },
) {
  return useQuery<
    { items: T[]; total: number; meta?: Record<string, unknown> },
    Error
  >({
    queryKey: tenantKey(key),
    queryFn: async () => {
      try {
        return await list<T>(path);
      } catch (err) {
        throw toApiError(err);
      }
    },
    ...options,
  });
}

export function useApiMutation<TInput, TResult>(
  key: string[],
  fn: (input: TInput) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation<TResult, Error, TInput>({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantKey(key) });
    },
  });
}

export { get, post, patch, remove };
